import { gql } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";
import {
  Accordion,
  ActionIcon,
  Button,
  Loader,
  Select,
  TextInput,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconTrash } from "@tabler/icons-react";
import { map } from "lodash";
import { DataTable } from "mantine-datatable";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import * as yup from "yup";
import {
  AggregationType,
  NumberConditionType,
  OrganizationIdentityProvider,
  PlayerCondition,
  PlayerConditionDataSource,
  PropertyCondition,
  RegistrationConditions,
  StringConditionType,
  Tournament,
} from "../../../../../gql/graphql";
import { CreationForm } from "../../../components/form/CreationForm";
import { PageHeader } from "../../../components/PageHeader";
import { useConfirmModal } from "../../../components/ConfirmModal";

const NUMBER_CONDITION_LABELS: Record<string, string> = {
  Bt: "Bigger than",
  Bte: "Bigger than or equal",
  Eq: "Equal",
  Lt: "Less than",
  Lte: "Less than or equal",
  Neq: "Not equal",
};

const STRING_CONDITION_LABELS: Record<string, string> = {
  Neq: "Not equal",
  Eq: "Equal",
};

const buildConditionTypeOptions = () => {
  const numberItems: { label: string; value: string }[] = [];
  map(NumberConditionType, (v, k) => {
    numberItems.push({
      label: NUMBER_CONDITION_LABELS[k] ?? k,
      value: "NUM_" + v,
    });
  });
  const stringItems: { label: string; value: string }[] = [];
  map(StringConditionType, (v, k) => {
    stringItems.push({
      label: STRING_CONDITION_LABELS[k] ?? k,
      value: "STR_" + v,
    });
  });
  return [
    { group: "Number conditions", items: numberItems },
    { group: "String conditions", items: stringItems },
  ];
};

const CONDITION_TYPE_OPTIONS = buildConditionTypeOptions();

export const UpdateTournamentRegistrationRules = () => {
  const tournamentId = useParams().tournamentId;
  const { confirm, modal } = useConfirmModal();
  const { loading: idpLoading, data: idpData } = useQuery<{
    identityProviders: OrganizationIdentityProvider[];
  }>(gql`
    query {
      identityProviders {
        ... on OrganizationIdentityProvider {
          id
          name
          configuration {
            ... on OAuthClientConfiguration {
              dataRetrievers {
                mappingConfiguration {
                  mappings {
                    mappedTo
                  }
                }
              }
            }
          }
        }
      }
    }
  `);
  const { loading, data } = useQuery<{ tournament: Tournament }>(
    gql`
      query ($id: ID!) {
        tournament(id: $id) {
          configuration {
            registrationConditions {
              memberConditions {
                condition {
                  numericCondition {
                    conditionType
                    value
                  }
                  propertyCondition
                  stringCondition {
                    value
                    conditionType
                  }
                  property
                }
                propertySource
                propertySourceId
              }
              teamConditions {
                stringCondition {
                  value
                  conditionType
                }
                propertyCondition
                property
                numericCondition {
                  value
                  conditionType
                  aggregationType
                  propertySource
                  propertySourceId
                }
              }
            }
          }
        }
      }
    `,
    { variables: { id: tournamentId }, skip: !tournamentId },
  );
  const [updateTournament] = useMutation<any>(gql`
    mutation (
      $id: ID!
      $configuration: UpdateTournamentConfigurationOrImportFromIdInput!
    ) {
      updateTournament(id: $id, input: { configuration: $configuration }) {
        id
      }
    }
  `);

  if (loading || idpLoading) {
    return <Loader />;
  }

  return (
    <div className="wp-fade-in">
      <PageHeader
        title="Registration Rules"
        showBackButton
        showBreadcrumbs={false}
      />
      {modal}
      <CreationForm
        title="Update registration rules"
        schemaDefinition={{
          registrationConditions: {
            type: "DYNAMIC",
            label: "Registration conditions",
            yupConfig: yup.object().shape({
              memberConditions: yup.array().of(
                yup.object().shape({
                  condition: yup.object().shape({
                    numericCondition: yup
                      .object()
                      .shape({
                        conditionType: yup
                          .string()
                          .oneOf(Object.values(NumberConditionType))
                          .required(),
                        value: yup.number().required(),
                      })
                      .default(undefined),
                    propertyCondition: yup
                      .string()
                      .oneOf(Object.values(PropertyCondition))
                      .required(),
                    stringCondition: yup
                      .object()
                      .shape({
                        value: yup.string().default(""),
                        conditionType: yup
                          .string()
                          .oneOf(Object.values(StringConditionType))
                          .required(),
                      })
                      .default(undefined),
                    property: yup.string().required(),
                  }),
                  propertySource: yup
                    .string()
                    .oneOf(Object.values(PlayerConditionDataSource))
                    .required(),
                  propertySourceId: yup.string().required(),
                }),
              ),
              teamConditions: yup.array().of(
                yup.object().shape({
                  condition: yup.object().shape({
                    numericCondition: yup
                      .object()
                      .shape({
                        conditionType: yup
                          .string()
                          .oneOf(Object.values(NumberConditionType))
                          .required(),
                        value: yup.number().required(),
                        aggregationType: yup
                          .string()
                          .oneOf(Object.values(AggregationType))
                          .required(),
                        propertySource: yup
                          .string()
                          .oneOf(Object.values(PlayerConditionDataSource))
                          .required(),
                        propertySourceId: yup.string().required(),
                      })
                      .default(undefined),
                    stringCondition: yup
                      .object()
                      .shape({
                        value: yup.string().default(""),
                        conditionType: yup
                          .string()
                          .oneOf(Object.values(StringConditionType))
                          .required(),
                      })
                      .default(undefined),
                    propertyCondition: yup.object().shape({
                      conditionType: yup
                        .string()
                        .oneOf(Object.values(PropertyCondition))
                        .required(),
                    }),
                    property: yup.string().required(),
                  }),
                }),
              ),
            }),
            defaultValue:
              data?.tournament?.configuration?.registrationConditions,

            element: (onChange) => {
              const [registrationConditions, setData] =
                useState<RegistrationConditions>(
                  Object.assign(
                    {
                      memberConditions: [],
                      teamConditions: [],
                    },
                    data?.tournament.configuration?.registrationConditions
                      ? {
                          memberConditions:
                            data?.tournament.configuration
                              ?.registrationConditions.memberConditions,
                          teamConditions:
                            data?.tournament.configuration
                              ?.registrationConditions.teamConditions,
                        }
                      : {},
                  ),
                );

              useEffect(() => {
                onChange(registrationConditions);
              }, [registrationConditions]);

              return (
                <>
                  <Accordion>
                    <Accordion.Item value={"data"}>
                      <Accordion.Control>Member conditions</Accordion.Control>
                      <Accordion.Panel>
                        <Button
                          onClick={() => {
                            setData({
                              ...registrationConditions,
                              memberConditions: (
                                [] as PlayerCondition[]
                              ).concat(
                                registrationConditions.memberConditions,
                                [
                                  {
                                    condition: {
                                      property: "",
                                      propertyCondition:
                                        PropertyCondition.Exists,
                                    },
                                    propertySource:
                                      PlayerConditionDataSource.Player,
                                  },
                                ],
                              ),
                            });
                          }}
                        >
                          Add member condition
                        </Button>
                        <DataTable
                          columns={[
                            {
                              accessor: "",
                              title: "Property",
                              render: (item: PlayerCondition, idx) =>
                                item.propertySource ===
                                PlayerConditionDataSource.Player ? (
                                  <TextInput
                                    value={item.condition.property}
                                    placeholder="Property identifier"
                                    onChange={(e) => {
                                      setData({
                                        ...registrationConditions,
                                        memberConditions:
                                          registrationConditions.memberConditions.map(
                                            (c, i) =>
                                              i === idx
                                                ? {
                                                    ...c,
                                                    condition: {
                                                      ...c.condition,
                                                      property: e.target.value,
                                                    },
                                                  }
                                                : c,
                                          ),
                                      });
                                    }}
                                  />
                                ) : (
                                  <Select
                                    data={[
                                      ...new Set(
                                        idpData?.identityProviders
                                          .find(
                                            (idp) =>
                                              idp.id === item.propertySourceId,
                                          )
                                          ?.configuration?.dataRetrievers.flatMap(
                                            (dr) =>
                                              dr.mappingConfiguration.mappings.map(
                                                (m) => m.mappedTo,
                                              ),
                                          ) ?? [],
                                      ),
                                    ]}
                                    value={item.condition.property}
                                    onChange={(value) => {
                                      if (!value) return;
                                      setData({
                                        ...registrationConditions,
                                        memberConditions:
                                          registrationConditions.memberConditions.map(
                                            (c, i) =>
                                              i === idx
                                                ? {
                                                    ...c,
                                                    condition: {
                                                      ...c.condition,
                                                      property: value,
                                                    },
                                                  }
                                                : c,
                                          ),
                                      });
                                    }}
                                  />
                                ),
                            },
                            {
                              accessor: "",
                              title: "Property Source",
                              render: (item, idx) => (
                                <Select
                                  value={
                                    item.propertySourceId ?? item.propertySource
                                  }
                                  onChange={(value) => {
                                    setData({
                                      ...registrationConditions,
                                      memberConditions:
                                        registrationConditions.memberConditions.map(
                                          (c, i) =>
                                            i === idx
                                              ? {
                                                  ...c,
                                                  propertySource:
                                                    value === "PLAYER"
                                                      ? PlayerConditionDataSource.Player
                                                      : PlayerConditionDataSource.IdentityProvider,
                                                  propertySourceId:
                                                    value === "PLAYER"
                                                      ? undefined
                                                      : value,
                                                }
                                              : c,
                                        ),
                                    });
                                  }}
                                  data={[
                                    {
                                      label: "Player",
                                      value: "PLAYER",
                                    },
                                  ].concat(
                                    idpData?.identityProviders.map((idp) => ({
                                      label: idp.name,
                                      value: idp.id,
                                    })) ?? [],
                                  )}
                                />
                              ),
                            },
                            {
                              accessor: "",
                              title: "Configuration",
                              render: (condition, index) => (
                                <>
                                  Property state:
                                  <br />
                                  <Select
                                    value={
                                      condition.condition.propertyCondition
                                    }
                                    onChange={(value) => {
                                      setData({
                                        ...registrationConditions,
                                        memberConditions:
                                          registrationConditions.memberConditions.map(
                                            (c, i) =>
                                              i === index
                                                ? {
                                                    ...c,
                                                    condition: {
                                                      ...c.condition,
                                                      propertyCondition:
                                                        value === "EXISTS"
                                                          ? PropertyCondition.Exists
                                                          : PropertyCondition.DontExist,
                                                    },
                                                  }
                                                : c,
                                          ),
                                      });
                                    }}
                                    data={map(PropertyCondition, (v, k) => ({
                                      label: k,
                                      value: v,
                                    }))}
                                  />
                                  <br />
                                  <br />
                                  {condition.condition.propertyCondition ===
                                    PropertyCondition.Exists && (
                                    <>
                                      Condition type:
                                      <br />
                                      <Select
                                        value={
                                          condition.condition.numericCondition
                                            ?.conditionType
                                            ? `NUM_${condition.condition.numericCondition.conditionType}`
                                            : condition.condition
                                                  .stringCondition
                                                  ?.conditionType
                                              ? `STR_${condition.condition.stringCondition.conditionType}`
                                              : ""
                                        }
                                        onChange={(value) => {
                                          if (!value) return;
                                          setData({
                                            ...registrationConditions,
                                            memberConditions:
                                              registrationConditions.memberConditions.map(
                                                (c, i) =>
                                                  i === index
                                                    ? {
                                                        ...c,
                                                        condition: {
                                                          ...c.condition,
                                                          numericCondition:
                                                            value.startsWith(
                                                              "NUM_",
                                                            )
                                                              ? {
                                                                  conditionType:
                                                                    value.substring(
                                                                      4,
                                                                    ) as NumberConditionType,
                                                                  value: 0,
                                                                }
                                                              : undefined,
                                                          stringCondition:
                                                            value.startsWith(
                                                              "STR_",
                                                            )
                                                              ? {
                                                                  conditionType:
                                                                    value.substring(
                                                                      4,
                                                                    ) as StringConditionType,
                                                                  value: "",
                                                                }
                                                              : undefined,
                                                        },
                                                      }
                                                    : c,
                                              ),
                                          });
                                        }}
                                        data={CONDITION_TYPE_OPTIONS}
                                        placeholder="Select condition type"
                                      />
                                      <br />
                                      Value: <br />
                                      <TextInput
                                        value={
                                          condition.condition.numericCondition?.value?.toString() ??
                                          condition.condition.stringCondition
                                            ?.value ??
                                          ""
                                        }
                                        type={
                                          condition.condition.stringCondition
                                            ? "text"
                                            : "number"
                                        }
                                        placeholder="Value to check against"
                                        onChange={(e) => {
                                          setData({
                                            ...registrationConditions,
                                            memberConditions:
                                              registrationConditions.memberConditions.map(
                                                (c, i) =>
                                                  i === index
                                                    ? {
                                                        ...c,
                                                        condition: {
                                                          ...c.condition,
                                                          numericCondition: c
                                                            .condition
                                                            .numericCondition
                                                            ? {
                                                                ...c.condition
                                                                  .numericCondition,
                                                                value:
                                                                  parseFloat(
                                                                    e.target
                                                                      .value,
                                                                  ),
                                                              }
                                                            : undefined,
                                                          stringCondition: c
                                                            .condition
                                                            .stringCondition
                                                            ? {
                                                                ...c.condition
                                                                  .stringCondition,
                                                                value:
                                                                  e.target
                                                                    .value,
                                                              }
                                                            : undefined,
                                                        },
                                                      }
                                                    : c,
                                              ),
                                          });
                                        }}
                                      />
                                    </>
                                  )}
                                </>
                              ),
                            },
                            {
                              accessor: "",
                              title: "",
                              render: (_, idx) => (
                                <ActionIcon
                                  onClick={async () => {
                                    const confirmed = await confirm({
                                      title: "Delete Condition",
                                      message:
                                        "Are you sure you want to delete this member condition?",
                                      type: "danger",
                                      confirmLabel: "Delete",
                                    });
                                    if (!confirmed) return;
                                    setData({
                                      ...registrationConditions,
                                      memberConditions:
                                        registrationConditions.memberConditions.filter(
                                          (_, i) => i !== idx,
                                        ),
                                    });
                                  }}
                                >
                                  <IconTrash />
                                </ActionIcon>
                              ),
                            },
                          ]}
                          records={registrationConditions.memberConditions}
                        />
                      </Accordion.Panel>
                    </Accordion.Item>
                  </Accordion>
                  <Accordion>
                    <Accordion.Item value={"default"}>
                      <Accordion.Control>Team conditions</Accordion.Control>
                      <Accordion.Panel>
                        <Button
                          onClick={() => {
                            setData({
                              ...registrationConditions,
                              teamConditions: [
                                ...registrationConditions.teamConditions,
                                {
                                  property: "",
                                  propertyCondition: PropertyCondition.Exists,
                                },
                              ],
                            });
                          }}
                        >
                          Add team condition
                        </Button>
                        <DataTable
                          columns={[
                            {
                              accessor: "",
                              title: "Property",
                              render: (condition, index) => (
                                <TextInput
                                  value={condition.property}
                                  onChange={(e) => {
                                    setData({
                                      ...registrationConditions,
                                      teamConditions:
                                        registrationConditions.teamConditions.map(
                                          (c, i) =>
                                            i === index
                                              ? {
                                                  ...c,
                                                  property: e.target.value,
                                                }
                                              : c,
                                        ),
                                    });
                                  }}
                                  placeholder="Property name"
                                />
                              ),
                            },
                            {
                              accessor: "",
                              title: "Configuration",
                              render: (condition, index) => (
                                <>
                                  Condition type:
                                  <br />
                                  <Select
                                    value={condition.propertyCondition}
                                    onChange={(value) => {
                                      setData({
                                        ...registrationConditions,
                                        teamConditions:
                                          registrationConditions.teamConditions.map(
                                            (c, i) =>
                                              i === index
                                                ? {
                                                    ...c,
                                                    propertyCondition:
                                                      value === "EXISTS"
                                                        ? PropertyCondition.Exists
                                                        : PropertyCondition.DontExist,
                                                  }
                                                : c,
                                          ),
                                      });
                                    }}
                                    data={map(PropertyCondition, (v, k) => ({
                                      label: k,
                                      value: v,
                                    }))}
                                  />
                                  {condition.propertyCondition ===
                                    PropertyCondition.Exists && (
                                    <>
                                      <br />
                                      Condition:
                                      <br />
                                      <Select
                                        value={
                                          condition.numericCondition
                                            ?.conditionType
                                            ? `NUM_${condition.numericCondition.conditionType}`
                                            : condition.stringCondition
                                                  ?.conditionType
                                              ? `STR_${condition.stringCondition.conditionType}`
                                              : ""
                                        }
                                        onChange={(value) => {
                                          if (!value) return;
                                          setData({
                                            ...registrationConditions,
                                            teamConditions:
                                              registrationConditions.teamConditions.map(
                                                (c, i) =>
                                                  i === index
                                                    ? {
                                                        ...c,
                                                        numericCondition:
                                                          value.startsWith(
                                                            "NUM_",
                                                          )
                                                            ? {
                                                                conditionType:
                                                                  value.substring(
                                                                    4,
                                                                  ) as NumberConditionType,
                                                                value: 0,
                                                              }
                                                            : undefined,
                                                        stringCondition:
                                                          value.startsWith(
                                                            "STR_",
                                                          )
                                                            ? {
                                                                conditionType:
                                                                  value.substring(
                                                                    4,
                                                                  ) as StringConditionType,
                                                                value: "",
                                                              }
                                                            : undefined,
                                                      }
                                                    : c,
                                              ),
                                          });
                                        }}
                                        data={CONDITION_TYPE_OPTIONS}
                                        placeholder="Select condition type"
                                      />
                                      <br />
                                      {condition.numericCondition && (
                                        <>
                                          Aggregation type:
                                          <br />
                                          <Select
                                            value={
                                              condition.numericCondition
                                                ?.aggregationType ?? "NONE"
                                            }
                                            onChange={(value) => {
                                              setData({
                                                ...registrationConditions,
                                                teamConditions:
                                                  registrationConditions.teamConditions.map(
                                                    (c, i) =>
                                                      i === index
                                                        ? {
                                                            ...c,
                                                            numericCondition:
                                                              c.numericCondition
                                                                ? {
                                                                    ...c.numericCondition,
                                                                    aggregationType:
                                                                      value ===
                                                                      "NONE"
                                                                        ? undefined
                                                                        : (value as
                                                                            | AggregationType
                                                                            | undefined),
                                                                  }
                                                                : undefined,
                                                            stringCondition:
                                                              c.stringCondition
                                                                ? {
                                                                    ...c.stringCondition,
                                                                    aggregationType:
                                                                      value ===
                                                                      "NONE"
                                                                        ? undefined
                                                                        : (value as
                                                                            | AggregationType
                                                                            | undefined),
                                                                  }
                                                                : undefined,
                                                          }
                                                        : c,
                                                  ),
                                              });
                                            }}
                                            data={[
                                              { label: "None", value: "NONE" },
                                              ...map(
                                                AggregationType,
                                                (v, k) => ({
                                                  label: k,
                                                  value: v,
                                                }),
                                              ),
                                            ]}
                                          />
                                          {condition.numericCondition
                                            .aggregationType && (
                                            <>
                                              <br />
                                              Aggregation property source:
                                              <br />
                                              <Select
                                                value={
                                                  condition.numericCondition
                                                    ?.propertySourceId ??
                                                  condition.numericCondition
                                                    ?.propertySource ??
                                                  "PLAYER"
                                                }
                                                onChange={(value) => {
                                                  setData({
                                                    ...registrationConditions,
                                                    teamConditions:
                                                      registrationConditions.teamConditions.map(
                                                        (c, i) =>
                                                          i === index
                                                            ? {
                                                                ...c,
                                                                numericCondition:
                                                                  c.numericCondition
                                                                    ? {
                                                                        ...c.numericCondition,
                                                                        propertySource:
                                                                          value ===
                                                                          "PLAYER"
                                                                            ? PlayerConditionDataSource.Player
                                                                            : PlayerConditionDataSource.IdentityProvider,
                                                                        propertySourceId:
                                                                          value ===
                                                                          "PLAYER"
                                                                            ? undefined
                                                                            : value,
                                                                      }
                                                                    : undefined,
                                                              }
                                                            : c,
                                                      ),
                                                  });
                                                }}
                                                data={[
                                                  {
                                                    label: "Player",
                                                    value: "PLAYER",
                                                  },
                                                  ...(idpData?.identityProviders.map(
                                                    (idp) => ({
                                                      label: `IDP: ${idp.name}`,
                                                      value: idp.id,
                                                    }),
                                                  ) ?? []),
                                                ]}
                                              />
                                            </>
                                          )}
                                        </>
                                      )}
                                      <br />
                                      Value: <br />
                                      <TextInput
                                        value={
                                          condition.numericCondition?.value?.toString() ??
                                          condition.stringCondition?.value ??
                                          ""
                                        }
                                        type={
                                          condition.stringCondition
                                            ? "text"
                                            : "number"
                                        }
                                        onChange={(e) => {
                                          setData({
                                            ...registrationConditions,
                                            teamConditions:
                                              registrationConditions.teamConditions.map(
                                                (c, i) =>
                                                  i === index
                                                    ? {
                                                        ...c,
                                                        numericCondition:
                                                          c.numericCondition
                                                            ? {
                                                                ...c.numericCondition,
                                                                value:
                                                                  parseFloat(
                                                                    e.target
                                                                      .value,
                                                                  ),
                                                              }
                                                            : undefined,
                                                        stringCondition:
                                                          c.stringCondition
                                                            ? {
                                                                ...c.stringCondition,
                                                                value:
                                                                  e.target
                                                                    .value,
                                                              }
                                                            : undefined,
                                                      }
                                                    : c,
                                              ),
                                          });
                                        }}
                                        placeholder="Value to check against"
                                      />
                                    </>
                                  )}
                                </>
                              ),
                            },
                            {
                              title: "",
                              accessor: "",
                              render: (_, index) => (
                                <ActionIcon
                                  onClick={async () => {
                                    const confirmed = await confirm({
                                      title: "Delete Condition",
                                      message:
                                        "Are you sure you want to delete this team condition?",
                                      type: "danger",
                                      confirmLabel: "Delete",
                                    });
                                    if (!confirmed) return;
                                    setData({
                                      ...registrationConditions,
                                      teamConditions:
                                        registrationConditions.teamConditions.filter(
                                          (_, i) => i !== index,
                                        ),
                                    });
                                  }}
                                >
                                  <IconTrash />
                                </ActionIcon>
                              ),
                            },
                          ]}
                          records={registrationConditions.teamConditions}
                        />
                      </Accordion.Panel>
                    </Accordion.Item>
                  </Accordion>
                </>
              );
            },
          },
        }}
        onSubmit={async (values) => {
          await updateTournament({
            variables: {
              id: tournamentId,
              configuration: {
                configuration: {
                  registrationConditions: values.registrationConditions,
                },
              },
            },
          }).then((data) => {
            if (data?.error) {
              throw new Error(data.error.message);
            }
          });
          notifications.show({
            title: "Tournament updated",
            message: "The tournament has been updated",
            color: "green",
            autoClose: 3000,
          });
        }}
      />
    </div>
  );
};
